const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1]!, 16),
        g: parseInt(result[2]!, 16),
        b: parseInt(result[3]!, 16),
      }
    : null;
};

export const isLightColour = (colour: string) => {
  if (!colour) {
    return false;
  }
  const rgb = hexToRgb(colour);
  // NB this currently doesn't work for 3-digit hex codes, so returning false for now
  if (!rgb) {
    return false;
  }
  const brightnessValue = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  return brightnessValue > 130;
};

/**
 * WCAG colour contrast calculation formula constants
 */
const WCAG_LUMINANCE_OFFSET = 0.05;
const RED_LUMINANCE_WEIGHT = 0.2126;
const GREEN_LUMINANCE_WEIGHT = 0.7152;
const BLUE_LUMINANCE_WEIGHT = 0.0722;
const SRGB_GAMMA_THRESHOLD = 0.04045;
const SRGB_LINEAR_DIVISOR = 12.92;
const SRGB_GAMMA_OFFSET = 0.055;
const SRGB_GAMMA_DIVISOR = 1.055;
const SRGB_GAMMA_EXPONENT = 2.4;

type Contrasts = {
  [choiceColour: string]: number;
};

export type BestContrastResult = {
  contrasts: Contrasts;
  best: {
    colour: string;
    contrast: number;
  };
};

/**
 * Calculates colour contrast to WCAG 2.0/2.1 spec
 * Takes a primary colour and an array of possible choices and returns contrast values for each combination with a best recommendation
 */
const getColourContrasts = (
  mainColour: string,
  choices: string[]
): BestContrastResult => {
  const initialContrasts: Contrasts = {};

  const {contrasts, best} = choices.reduce(
    (acc, choice) => {
      const contrast = checkContrast(mainColour, choice);
      acc.contrasts[choice] = contrast;

      if (contrast > acc.best.contrast) {
        acc.best = {colour: choice, contrast};
      }

      return acc;
    },
    {
      contrasts: initialContrasts,
      best: {colour: mainColour, contrast: 1},
    }
  );

  return {contrasts, best};
};

/**
 * Calculates colour contrast to WCAG 2.0/2.1 spec and returns best choice
 * Takes a primary colour and an array of possible choices
 */
export const getBestContrastingColour = (
  mainColour: string,
  choices: string[]
) => {
  if (!mainColour || !choices || choices.length === 0) {
    return mainColour;
  }

  const {best} = getColourContrasts(mainColour, choices);
  return best.colour;
};

/**
 * Calculates colour contrast to WCAG 2.0/2.1 spec
 * Returns a contrast ratio between 1 and 21
 */
const checkContrast = (colour1: string, colour2: string) => {
  const c1Luma = getLuminance(colour1);
  const c2Luma = getLuminance(colour2);

  const contrast =
    c2Luma > c1Luma
      ? (c2Luma + WCAG_LUMINANCE_OFFSET) / (c1Luma + WCAG_LUMINANCE_OFFSET)
      : (c1Luma + WCAG_LUMINANCE_OFFSET) / (c2Luma + WCAG_LUMINANCE_OFFSET);
  return Math.floor(contrast * 10) / 10;
};

const getLuminance = (colour: string) => {
  let currentColour = colour.replace('#', '');
  if (currentColour.length === 3) {
    // Expand shorthand hex to full form, e.g. #abc -> #aabbcc
    currentColour = currentColour
      .split('')
      .map((x) => x + x)
      .join('');
  }
  const r = parseInt(currentColour.slice(0, 2), 16) / 255;
  const g = parseInt(currentColour.slice(2, 4), 16) / 255;
  const b = parseInt(currentColour.slice(4, 6), 16) / 255;

  const rLuma = getRelativeLuminance(r);
  const gLuma = getRelativeLuminance(g);
  const bLuma = getRelativeLuminance(b);

  const luminance =
    RED_LUMINANCE_WEIGHT * rLuma +
    GREEN_LUMINANCE_WEIGHT * gLuma +
    BLUE_LUMINANCE_WEIGHT * bLuma;
  return luminance;
};

const getRelativeLuminance = (channel: number) =>
  // applies the sRGB gamma correction formula where required
  channel <= SRGB_GAMMA_THRESHOLD
    ? channel / SRGB_LINEAR_DIVISOR
    : Math.pow(
        (channel + SRGB_GAMMA_OFFSET) / SRGB_GAMMA_DIVISOR,
        SRGB_GAMMA_EXPONENT
      );
